export type YesNo = 'yes' | 'no' | 'maybe';

export interface DeepSide {
  love: string;
  career: string;
  finance: string;
  advice: string;
  yesno: YesNo;
}

export interface Meaning {
  keywords: string[];
  upright: string;
  reversed: string;
  deep?: { upright: DeepSide; reversed: DeepSide };
}

export interface PullResponse {
  cardId: number;
  reversed: boolean;
  alreadyPulled: boolean;
  card: { name: string; name_en: string; name_pl: string; image: string };
  meaning: Meaning;
  lang: string;
}

function getInitData(): string {
  // @ts-expect-error — Telegram injects this script global
  const tg = window.Telegram?.WebApp;
  return tg?.initData ?? '';
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'content-type': 'application/json',
      'x-telegram-init-data': getInitData(),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  return res.json() as Promise<T>;
}

export function pullDailyCard(languageCode?: string): Promise<PullResponse> {
  return call<PullResponse>('/api/pull', {
    method: 'POST',
    body: JSON.stringify({ language_code: languageCode }),
  });
}

export function setLang(lang: string): Promise<{ ok: true }> {
  return call('/api/set-lang', { method: 'POST', body: JSON.stringify({ lang }) });
}

export interface ProductView {
  id: string;
  priceStars: number;
  recurring: boolean;
  title: string;
  description: string;
}

export function fetchProducts(): Promise<{ products: ProductView[]; lang: string }> {
  return call('/api/products', { method: 'GET' });
}

export function createInvoice(productId: string): Promise<{ invoiceLink: string }> {
  return call('/api/invoice', { method: 'POST', body: JSON.stringify({ productId }) });
}

export function extraPull(): Promise<PullResponse> {
  return call('/api/extra-pull', { method: 'POST' });
}

export type SpreadType = '3-card' | 'celtic' | 'love' | 'career' | 'horseshoe' | 'month' | 'year';

export interface SpreadResponse {
  type: SpreadType;
  positions: string[];
  cards: {
    cardId: number;
    reversed: boolean;
    position: string | null;
    card: { name: string; name_en: string; name_pl: string; image: string };
    meaning: Meaning;
  }[];
  lang: string;
}

export function spread(type: SpreadType): Promise<SpreadResponse> {
  return call('/api/spread', { method: 'POST', body: JSON.stringify({ type }) });
}

export interface YesNoResponse {
  cardId: number;
  reversed: boolean;
  answer: YesNo;
  card: { name: string; name_en: string; name_pl: string; image: string };
  meaning: Meaning;
  lang: string;
}

export function yesNo(): Promise<YesNoResponse> {
  return call('/api/yesno', { method: 'POST' });
}

export interface DeepenResponse {
  cardId: number;
  reversed: boolean;
  card: { name: string; name_en: string; name_pl: string; image: string };
  deep: DeepSide;
  lang: string;
}

export function deepen(): Promise<DeepenResponse> {
  return call('/api/deepen', { method: 'POST' });
}

export interface ExtraResultData {
  cardId: number;
  reversed: boolean;
  card: { name: string; name_en: string; name_pl: string; image: string };
  meaning: Meaning;
  lang: string;
}

export interface YesNoResultData {
  cardId: number;
  reversed: boolean;
  answer: YesNo;
  card: { name: string; name_en: string; name_pl: string; image: string };
  meaning: Meaning;
  lang: string;
}

export type TodayResult =
  | { kind: 'deepen'; data: DeepenResponse }
  | { kind: 'extra'; data: ExtraResultData }
  | { kind: 'yesno'; data: YesNoResultData }
  | { kind: 'spread'; data: SpreadResponse };

export function fetchTodayPulls(): Promise<{ results: TodayResult[]; lang: string; adFree: boolean }> {
  return call('/api/today-pulls', { method: 'GET' });
}
